import { FC, Fragment, Suspense, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Box } from "@mui/material";
import { Loader, SideBarNav } from "Components";
import RouteFallback from "RouteFallback";
import TimerProvider from "Context/TimerContext";
import { useParams } from "react-router-dom";
import PatientApi from "Service/Patient.api";
import EhrApi from "Service/Ehr.api";
import { CheckStringEmpty, CheckStringId } from "Utils/common";
import { useNavigate } from "react-router-dom";
import PatientFormatter from "Utils/Patient.Formatter";
import { useDispatch } from "react-redux";
import {
  addEligiblePrgmArr,
  addPatientInfo,
  addPatientOrgInfo,
  clearEligiblePrgmArr,
  clearPatientInfo,
  clearPatientOrgInfo,
  clearPatientEhr,
  setPatientEhr,
} from "Redux/Patient/Action";
import { RouteUrls } from "Constant/RouteUrls";
import CommonFormatter from "Utils/Common.Formatter";
import PrgmFormatter from "Utils/Prgm.Formatter";
import { PrgmListResType } from "DataTypes/Services/Prgm.type";
import { ReduxScreenNavType } from "DataTypes/Redux";
import { setNavArr } from "Redux/Screen/Action";
import { store } from "Redux/Store";

const PatientManagement: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { patientId } = useParams();
  const newPatientId = CheckStringId(patientId);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return () => {
      dispatch(clearPatientInfo());
      dispatch(clearPatientOrgInfo());
      dispatch(clearEligiblePrgmArr());
      dispatch(clearPatientEhr());
      const { navArr } = store.getState().screen;
      dispatch(
        setNavArr(
          navArr.filter((el: ReduxScreenNavType) => el.navType !== "patient")
        )
      );
    };
  }, []);

  useEffect(() => {
    fetchPatientInfo();
  }, [patientId]);

  const fetchPatientInfo: () => void = async () => {
    setLoading(true);
    const patientInfoRes = await PatientApi.demographic({ id1: newPatientId });
    const clinicalNotesRes = await PatientApi.PatientClinicalNotesSearch({
      id1: newPatientId,
    });
    let notes = "";
    if (clinicalNotesRes?.success) {
      notes = clinicalNotesRes?.data?.alertNotes;
    }
    const patientPrgmArr = await PatientApi.PatientPrgmSearch({
      id1: newPatientId,
    });
    let prgmList: PrgmListResType[] = [];
    if (patientPrgmArr?.success) {
      prgmList = PrgmFormatter.PrgmListRes(patientPrgmArr?.data);
    }
    if (patientInfoRes?.success) {
      const patientData = { ...patientInfoRes?.data?.patients, ehrPatientId: patientInfoRes?.data?.ehrPatientId, lastEhrVisitDate: patientInfoRes?.data?.lastEhrVisitDate, notes: notes };
      const formateRes = PatientFormatter.FormatePatientInfo(patientData);
      dispatch(addPatientInfo(formateRes));
      dispatch(addEligiblePrgmArr(prgmList));
      CommonFormatter.HandleNavArr({
        id: "patient-details",
        label: formateRes.name,
        icon: "patient-notes",
        path: `/${RouteUrls.hs}/${RouteUrls.patient}/` +
          `${formateRes.id}/${RouteUrls.details}`,
        level: 2,
        link: RouteUrls.details,
        navType: "patient",
      });
    } else navigate(-1);
    const patientOrgInfoRes = await PatientApi.practice({ id1: newPatientId });
    if (patientOrgInfoRes?.success) {
      const formateRes = PatientFormatter.FormatPatientOrg(
        patientOrgInfoRes?.data?.practiceLocations
      );
      dispatch(addPatientOrgInfo(formateRes));
    } else navigate(-1);
    // Deliberately not awaited: which EHR Sync buttons render is a detail of
    // the patient screen, and the EHR service being slow or down must not hold
    // up - or break - the screen itself.
    fetchEhrCapabilities();
    setLoading(false);
  };

  /**
   * Loads which domains this patient's practice location can sync, and whether
   * each one is currently available. The domain list is vendor-specific, so
   * this response is the only thing that decides which EHR Sync buttons render.
   *
   * A failure is not surfaced to the user: the EHR service being unreachable
   * simply means no sync buttons, never a broken patient screen.
   */
  const fetchEhrCapabilities: () => void = async () => {
    // Wipe first. This runs on every patient-screen mount, so it also clears
    // anything left in the persisted store - including an activeSyncDomain
    // stuck by a refresh mid-sync, which would otherwise spin forever.
    dispatch(clearPatientEhr());
    if (!newPatientId) return;
    const res = await EhrApi.getEHRCapabilities({ id1: newPatientId });
    if (res?.success && res?.data) {
      dispatch(
        setPatientEhr({
          vendor: CheckStringEmpty(res?.data?.vendor) || null,
          enabled: Boolean(res?.data?.enabled),
          syncDomains: Array.isArray(res?.data?.syncDomains)
            ? res.data.syncDomains
            : [],
        })
      );
    }
  };

  return (
    <Box display="flex" flexDirection="row" height="91%">
      {loading ? (
        <Loader />
      ) : (
        <Fragment>
          <SideBarNav />
          <TimerProvider>
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </TimerProvider>
        </Fragment>
      )}
    </Box>
  );
};
export default PatientManagement;
